import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, ADMIN_REDIRECT_PATH } from '../utils/rbac';
import { useAccess, Capability } from '../hooks/useAccess';

type AdminScope = 'legacy' | 'firm' | 'firm_audit' | 'golab' | 'platform';

interface AdminRouteProps {
  /**
   * Which privilege this subtree requires:
   * - 'legacy' (default): the pre-RBAC `role === 'admin'` check only. This is
   *   the historical behavior — unchanged for every existing usage (platform/
   *   cross-org admin, etc.), which stay legacy-gated.
   * - 'firm': the CLIENT firm-administration surface — legacy admins OR holders
   *   of the `firm.manage` capability (FIRM_ADMIN, i.e. Organization Admin).
   *   Used by the Settings admin routes so an Organization Admin can reach firm
   *   administration (including the audit trail, which firm.manage covers).
   * - 'firm_audit': the audit trail specifically — legacy admins OR holders of
   *   `firm.audit_view` (or firm.manage / platform.manage, which subsume it).
   *   Matches the nav gate and the backend's FIRM_AUDIT_VIEW check, so a
   *   principal granted read-only audit access alone still reaches the page
   *   instead of being bounced to the redirect (which the plain 'firm' scope,
   *   requiring firm.manage, would do).
   * - 'golab': the GO Lab surface — legacy admins OR holders of a GO Lab
   *   capability (PLATFORM_ADMIN or any LAB_* role). Gated on `golab.annotate`,
   *   the base capability every internal GO Lab role holds.
   * - 'platform': platform administration — legacy admins OR holders of
   *   `platform.manage` (PLATFORM_ADMIN). Used by the cross-org / org-onboarding
   *   surfaces that manage every organization.
   */
  require?: AdminScope;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ require = 'legacy' }) => {
  const { user, loading } = useAuth();
  const { can, isLoading } = useAccess();

  if (loading) {
    return <div>Loading...</div>;
  }

  const legacy = isAdmin(user);

  if (require === 'firm') {
    // Capabilities load asynchronously; wait rather than bounce a valid
    // FIRM_ADMIN to /v4/records before /rbac/me has resolved (e.g. on refresh).
    if (isLoading) {
      return <div>Loading...</div>;
    }
    // Platform admins administer every organization's surfaces (Sep-3).
    if (legacy || can(Capability.FirmManage) || can(Capability.PlatformManage)) {
      return <Outlet />;
    }
    return <Navigate to={ADMIN_REDIRECT_PATH} replace />;
  }

  if (require === 'firm_audit') {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    // Audit read access (firm.audit_view) is enough; firm.manage and
    // platform.manage subsume it, and legacy admins keep their bypass — so
    // the guard admits exactly who the nav shows and the backend allows.
    if (legacy || can(Capability.FirmAuditView) || can(Capability.FirmManage) || can(Capability.PlatformManage)) {
      return <Outlet />;
    }
    return <Navigate to={ADMIN_REDIRECT_PATH} replace />;
  }

  if (require === 'golab') {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    // Sep-3: no legacy-admin bypass — GO Lab is internal (LAB_* / platform).
    if (can(Capability.GolabAnnotate)) {
      return <Outlet />;
    }
    return <Navigate to={ADMIN_REDIRECT_PATH} replace />;
  }

  if (require === 'platform') {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    // Platform administration manages EVERY organization, so require
    // platform.manage specifically — NOT any legacy `role === 'admin'` (a
    // client-org legacy admin lacks platform.manage and would only hit a page
    // whose backend 403s them). This matches the button's own visibility gate.
    if (can(Capability.PlatformManage)) {
      return <Outlet />;
    }
    // Send denials to the capability-aware landing rather than the monitoring
    // surface a lab-only user may not be able to use.
    return <Navigate to="/v4" replace />;
  }

  // Legacy scope: unchanged pre-RBAC behavior — decided purely on the JWT role,
  // so GO Lab / platform guards keep their existing timing and semantics.
  if (!legacy) return <Navigate to={ADMIN_REDIRECT_PATH} replace />;
  return <Outlet />;
};

export default AdminRoute;
